import { useCallback } from "react";

import ACL_RELATIONSHIPS from "acl-relationships";
import usePageTitle from "hooks/usePageTitle";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import withRoleCheck from "components/hocs/withRoleCheck";
import { checkIsAuthorized, getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import EntityService from "services/Entity";
import { invalidateEntitiesQuery, useEntityTable } from "hooks/useEntityTable";
import _MergeEntitiesModal from "routes/entity/components/MergeEntitiesModal";
import { NewEntityModal as _NewEntityModal } from "routes/entity/components/Modals";

export const ENTITIES_TABLE_CONSTANTS = {
  headerIcon: "building",
  header: "Entities",
  tableName: "entities",
  queryKey: "entity",
  className: "Entity",
  exportTableName: "EntitiesTable",
};

const NewEntityModal = withRoleCheck(_NewEntityModal, [
  ACL_RELATIONSHIPS.entity.create,
  ACL_RELATIONSHIPS.user.read,
]);
const MergeEntitiesModal = withRoleCheck(_MergeEntitiesModal, [
  ACL_RELATIONSHIPS.entityMerge.create,
]);

const enableMerge = checkIsAuthorized([ACL_RELATIONSHIPS.entityMerge.create]);

const mergeButton = ({ selectedData, uncheckAll }) => {
  const handleFinish = async () => {
    uncheckAll();
    await invalidateEntitiesQuery();
  };

  return (
    <MergeEntitiesModal entities={selectedData} finishMerge={handleFinish} />
  );
};

const EntitiesTableV3 = () => {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.entityTags.create,
      ACL_RELATIONSHIPS.entityTags.read,
      ACL_RELATIONSHIPS.entityTags.delete,
    ],
    { partialMatch: true }
  );

  usePageTitle(ENTITIES_TABLE_CONSTANTS.header);

  const { filters, columns, customFields } = useEntityTable();

  const fetchEntities = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);

        const response = await EntityService.getEntities(nextQueryParams);
        return response;
      } catch (error) {
        console.error("Error fetching entities:", error);
        throw error;
      }
    },
    [columns]
  );

  return (
    <BaseTableV3
      columns={columns}
      filters={filters}
      customFields={customFields}
      headerIcon={ENTITIES_TABLE_CONSTANTS.headerIcon}
      header={ENTITIES_TABLE_CONSTANTS.header}
      tableName={ENTITIES_TABLE_CONSTANTS.tableName}
      queryKey={ENTITIES_TABLE_CONSTANTS.queryKey}
      className={ENTITIES_TABLE_CONSTANTS.className}
      exportTableName={ENTITIES_TABLE_CONSTANTS.exportTableName}
      createButton={<NewEntityModal fetchEntities={invalidateEntitiesQuery} />}
      fetchData={fetchEntities}
      mergeButton={enableMerge ? mergeButton : null}
      invalidateData={invalidateEntitiesQuery}
      enableSearch
      enableSettings
      tagsDropdownParams={{ visible: enableTags }}
    />
  );
};

export default EntitiesTableV3;
